import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { get, find, isString, map, findIndex, isEqual, forEach, indexOf, isEmpty } from 'lodash';
import { Form } from 'react-bootstrap';

import ChartComponent from '../ChartComponent';

import { DEFAULT_SELECTED_ELEMENTS } from '../../../api/Graphics/graphic-operations';

const CARD_MARGIN = 5;
const TOP_MARGIN = 15;
const LEFT_MARGIN = 100;
const BOTTOM_MARGIN = 65;
const RIGHT_MARGIN = 100;
const STACKED_LAYOUT = 'stacked';
const GROUPED_LAYOUT = 'grouped';
const TEXT_INICIAL_SIZE = 10;
const CHART_CLASS = 'stacked-to-grouped-bar-chart';

class StackedToGroupBarChart extends ChartComponent {
  id = isEmpty(this.props.atlasID)
    ? `${CHART_CLASS}-card-${this.props.isCard}`
    : this.props.atlasID;

  x = {};

  y = {};

  yMax = {};

  y1Max = {};

  xAxis = {};

  yAxis = {};

  rect = {};

  svg = {};

  state = {
    layout: STACKED_LAYOUT
  };

  componentDidUpdate(prevProps, prevState) {
    const { layout } = this.state;
    const { layout: prevLayout } = prevState;

    if (!isEqual(layout, prevLayout)) {
      if (layout === STACKED_LAYOUT) this.transitionStacked();
      if (layout === GROUPED_LAYOUT) this.transitionGrouped();
      this.paintAxis(layout);
    } else {
      this.deleteChart();
      this.drawChart();
    }
  }

  changeLayout = event => this.setState({ layout: event.target.id });

  drawChart = () => {
    const {
      alpha,
      borderAlpha,
      borderColor,
      colorRange,
      dataNamed,
      dataNumeric,
      height,
      isCard,
      numericName,
      selectedElements,
      weight,
      width
    } = this.props;
    const { layout } = this.state;

    const margin = {
      top: isCard ? 5 : 15,
      left: isCard ? 5 : 100,
      bottom: isCard ? 5 : 65,
      right: isCard ? 5 : 100
    };

    const numericNamesAsObjects = map(numericName, name => ({ name }));
    const data = map(dataNamed, (namedValue, namedIndex) => {
      const objectToReturn = {
        name: namedValue
      };
      forEach(dataNumeric, (dataNumericSubarray, numericSubarrayIndex) => {
        objectToReturn[numericName[numericSubarrayIndex]] = dataNumericSubarray[namedIndex];
      });
      return objectToReturn;
    });

    this.svg = d3
      .select(`#svg-${this.id}`)
      .attr('viewBox', [0, 0, width, height - (isCard ? 0 : 48)]);

    const series = d3
      .stack()
      .keys(numericName)(data)
      // eslint-disable-next-line no-return-assign, no-sequences, no-param-reassign
      .map(d => (d.forEach(v => (v.key = d.key)), d));

    this.yMax = d3.max(dataNumeric, d => d3.max(d, d2 => Number(d2)));

    this.y1Max = d3.max(series, d => d3.max(d, d2 => d2[1]));

    this.x = d3
      .scaleBand()
      .domain(map(data, d => d.name))
      .rangeRound([margin.left, width - margin.right])
      .padding(0.08);

    this.y = d3
      .scaleLinear()
      .domain([0, this.y1Max])
      .nice()
      .range([height - margin.bottom - (isCard ? 0 : 48), margin.top]);

    this.xAxis = selectedElements.Text
      ? d3 /* eslint-disable indent */
          .axisBottom(this.x)
          .tickSizeOuter(0) /* eslint-enable indent */
      : d3.axisBottom(this.x).tickValues([]);

    this.yAxis = selectedElements.Text
      ? d3 /* eslint-disable indent */
          .axisLeft(this.y)
          .ticks() /* eslint-enable indent */
      : d3.axisLeft(this.y).tickValues([]);

    this.rect = this.svg
      .append('g')
      .attr('class', `${this.id}`)
      .selectAll('g')
      .data(series)
      .join('g')
      .selectAll('rect')
      .data(d => d)
      .join('rect')
      .attr('class', `${this.id}`)
      .attr('fill', (d, i) => {
        const numericNameIndex = findIndex(numericNamesAsObjects, ['name', d.key]);

        if (layout === STACKED_LAYOUT) {
          if (isString(colorRange)) return colorRange;
          if (isString(colorRange[numericNameIndex])) return colorRange[numericNameIndex];
          return colorRange[numericNameIndex][i];
        }

        if (isString(colorRange)) return colorRange;
        if (isString(colorRange[numericNameIndex])) return colorRange[numericNameIndex];
        const dataNumericIndex = indexOf(dataNumeric[numericNameIndex], `${d[1] - d[0]}`);
        return colorRange[numericNameIndex][dataNumericIndex];
      })
      .attr('fill-opacity', d => alpha[findIndex(numericNamesAsObjects, ['name', d.key])])
      .attr('stroke', d => borderColor[findIndex(numericNamesAsObjects, ['name', d.key])])
      .attr('stroke-opacity', d => borderAlpha[findIndex(numericNamesAsObjects, ['name', d.key])])
      .attr('stroke-width', d => weight[findIndex(numericNamesAsObjects, ['name', d.key])]);

    if (layout === STACKED_LAYOUT) this.paintStacked();
    if (layout === GROUPED_LAYOUT) this.paintGrouped();

    this.paintAxis();
  };

  paintAxis() {
    const { globalProperties, isCard, selectedElements } = this.props;

    const margin = {
      top: isCard ? CARD_MARGIN : TOP_MARGIN,
      left: isCard ? CARD_MARGIN : LEFT_MARGIN,
      bottom: isCard ? CARD_MARGIN : BOTTOM_MARGIN,
      right: isCard ? CARD_MARGIN : RIGHT_MARGIN
    };

    if (!isCard) {
      this.rect.insert('title').text(d => `${d.data.name} ${d.key}: ${d.data[d.key]}`);
      d3.selectAll(`.${this.id}-axis`).remove();

      this.svg
        .append('g')
        .attr('class', `${this.id} ${this.id}-axis`)
        .attr('transform', `translate(0,${this.y(0)})`)
        .call(this.xAxis);

      this.svg
        .selectAll('.tick')
        .selectAll('text')
        .style('text-anchor', 'start')
        .attr(
          'transform',
          `rotate(${get(
            find(globalProperties, ['name', 'xAxisTextAngle']),
            'value',
            TEXT_INICIAL_SIZE
          )})`
        );

      this.svg
        .append('g')
        .attr('class', `${this.id} ${this.id}-axis`)
        .attr('transform', `translate(${margin.left},0)`)
        .call(this.yAxis);

      this.svg
        .selectAll('.tick')
        .selectAll('text')
        .attr(
          'font-size',
          get(find(globalProperties, ['name', 'textSize']), 'value', TEXT_INICIAL_SIZE)
        );

      if (!selectedElements.Axis) {
        this.svg.selectAll('.domain').remove();
      }
    }
  }

  paintGrouped() {
    const { numericName } = this.props;
    this.y.domain([0, this.yMax]);
    this.rect
      .attr(
        'x',
        d =>
          this.x(d.data.name) +
          (this.x.bandwidth() / numericName.length) * indexOf(numericName, d.key)
      )
      .attr('width', this.x.bandwidth() / numericName.length)
      .attr('y', d => this.y(d[1] - d[0]))
      .attr('height', d => this.y(0) - this.y(d[1] - d[0]));
  }

  paintStacked() {
    this.y.domain([0, this.y1Max]);
    this.rect
      .attr('y', d => Math.min(this.y(d[0]), this.y(d[1])))
      .attr('height', d => Math.abs(this.y(d[0]) - this.y(d[1])))
      .attr('x', d => this.x(d.data.name))
      .attr('width', this.x.bandwidth());
  }

  transitionGrouped() {
    const { numericName } = this.props;

    this.y.domain([0, this.yMax]);

    this.rect
      .transition()
      .duration(500)
      .delay((d, i) => i * 20)
      .attr(
        'x',
        d =>
          this.x(d.data.name) +
          (this.x.bandwidth() / numericName.length) * indexOf(numericName, d.key)
      )
      .attr('width', this.x.bandwidth() / numericName.length)
      .transition()
      .attr('y', d => this.y(d[1] - d[0]))
      .attr('height', d => this.y(0) - this.y(d[1] - d[0]));
  }

  transitionStacked() {
    this.y.domain([0, this.y1Max]);

    this.rect
      .transition()
      .duration(500)
      .delay((d, i) => i * 20)
      .attr('y', d => Math.min(this.y(d[0]), this.y(d[1])))
      .attr('height', d => Math.abs(this.y(d[0]) - this.y(d[1])))
      .transition()
      .attr('x', d => this.x(d.data.name))
      .attr('width', this.x.bandwidth());
  }

  render() {
    const { isCard } = this.props;
    const { layout } = this.state;
    return (
      <>
        {!isCard && (
          <Form.Check
            custom
            type="radio"
            id="stacked"
            label="Stacked"
            checked={layout === STACKED_LAYOUT}
            onChange={this.changeLayout}
          />
        )}
        {!isCard && (
          <Form.Check
            custom
            type="radio"
            id="grouped"
            label="Grouped"
            checked={layout === GROUPED_LAYOUT}
            onChange={this.changeLayout}
          />
        )}
        <svg id={`svg-${this.id}`} />
      </>
    );
  }
}

StackedToGroupBarChart.defaultProps = {
  width: 0,
  height: 0,
  dataNamed: ['A', 'B', 'C'],
  dataNumeric: [
    [1, 1.5, 2],
    [1.1, 1.5, 2],
    [1.2, 1.5, 2]
  ],
  colorRange: ['rgb(181,215,232)', 'rgb(181,232,212)', 'rgb(182,213,246'],
  alpha: [0.9, 0.9, 0.9],
  weight: [1, 1, 1],
  numericName: ['AtributoA', 'AtributoB', 'AtributoC'],
  borderColor: ['black', 'black', 'black'],
  borderAlpha: [0.9, 0.9, 0.9],
  selectedElements: DEFAULT_SELECTED_ELEMENTS,
  globalProperties: [],
  isCard: false
};
StackedToGroupBarChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  dataNumeric: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
  ]),
  dataNamed: PropTypes.array,
  colorRange: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]))
  ]),
  alpha: PropTypes.arrayOf(PropTypes.number),
  weight: PropTypes.arrayOf(PropTypes.number),
  borderColor: PropTypes.arrayOf(PropTypes.string),
  borderAlpha: PropTypes.arrayOf(PropTypes.number),
  selectedElements: PropTypes.object,
  globalProperties: PropTypes.array,
  numericName: PropTypes.arrayOf(PropTypes.string),
  isCard: PropTypes.bool
};

export default StackedToGroupBarChart;
